import React, { forwardRef, useCallback, useEffect, useRef, useState } from "react"
import { Input, Label } from "@ui/shad"
import { cn } from "@ui/utils"

interface NumberInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value"> {
  id: string
  label?: string
  error?: boolean
  value?: number | null
  onChange?: (value: number | null) => void
  helperText?: string
  decimalPlaces?: number
  commitOnBlur?: boolean
}

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    { label, error, id, onBlur, onChange, onFocus, value, helperText, decimalPlaces, commitOnBlur = false, ...props },
    ref
  ) => {
    const formatValue = useCallback(
      (value: number | null | undefined) =>
        value == null ? "" : decimalPlaces === undefined ? value.toString() : value.toFixed(decimalPlaces),
      [decimalPlaces]
    )
    const [inputValue, setInputValue] = useState(formatValue(value))
    const isEditing = useRef(false)
    const lastEmittedValue = useRef<number | null | undefined>(undefined)

    useEffect(() => {
      if (isEditing.current && Object.is(value, lastEmittedValue.current)) return

      lastEmittedValue.current = undefined
      setInputValue(formatValue(value))
    }, [value, formatValue])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value

      const numericValue = nextValue === "" ? null : Number(nextValue)

      setInputValue(nextValue)
      if (commitOnBlur) return

      lastEmittedValue.current = numericValue
      onChange?.(numericValue)
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      isEditing.current = true
      onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      isEditing.current = false
      lastEmittedValue.current = undefined

      if (commitOnBlur) {
        const numericValue = inputValue === "" ? null : Number(inputValue)

        setInputValue(formatValue(numericValue))
        onChange?.(numericValue)
      } else {
        setInputValue(formatValue(value))
      }

      onBlur?.(e)
    }

    return (
      <div className="space-y-1">
        {label && (
          <Label htmlFor={id} className={cn(error && "text-destructive")}>
            {label}
          </Label>
        )}
        <Input
          type="number"
          ref={ref}
          id={id}
          onBlur={handleBlur}
          onChange={handleChange}
          onFocus={handleFocus}
          value={inputValue}
          {...props}
        />
        {helperText && <p className="text-muted-foreground text-xs">{helperText}</p>}
      </div>
    )
  }
)

NumberInput.displayName = "NumberInput"
